import { createClient } from '@supabase/supabase-js';

const supabaseUrl     = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase credentials. Check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

// Single client for all operations — anon key works for everything because
// RLS is disabled on all app tables (internal tool, no per-user row restrictions).
// The service role key is intentionally NOT used client-side: Supabase blocks it
// in browser contexts with "Forbidden use of secret API key in browser".
export const supabase = createClient(supabaseUrl, supabaseAnonKey);
const supabaseWrite   = supabase;

async function fetchAllPaginatedRows(buildQuery, pageSize = 1000, maxRows = 50000) {
  const rows = [];

  for (let from = 0; from < maxRows; from += pageSize) {
    const to = from + pageSize - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;

    const batch = data || [];
    rows.push(...batch);

    if (batch.length < pageSize) break;
  }

  return rows;
}

function flattenSubmission(s) {
  const txnDate = s.transactions?.collection_date ?? s.preserved_txn_date ?? null;
  const txnStatus = s.transactions?.status ?? s.preserved_txn_status ?? null;
  const txnAmount = s.transactions?.amount_to_collect ?? s.preserved_txn_amount_to_collect ?? null;
  const txnDiff = s.transactions?.diff ?? s.preserved_txn_diff ?? null;

  return {
    ...s,
    txn_date: txnDate,
    txn_diff: txnStatus === 'today_direct' ? null : txnDiff,
    txn_amount_to_collect: txnStatus === 'today_direct' ? null : txnAmount,
    is_direct: txnStatus === 'today_direct',
    is_archived: !!s.archived_at,
  };
}

async function getTransactionSnapshot(transactionDbId) {
  const { data, error } = await supabase
    .from('transactions')
    .select('collection_date, amount_to_collect, diff, status, upload_id, transaction_id')
    .eq('id', transactionDbId)
    .single();
  if (error) throw error;
  return data;
}

// ─── UPLOADS ──────────────────────────────────────────────────────────────────

export async function createUpload(filename) {
  const { data, error } = await supabaseWrite
    .from('uploads')
    .insert({ filename, upload_date: new Date().toISOString().split('T')[0] })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getLatestUpload() {
  const { data, error } = await supabase
    .from('uploads')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

export async function getAllUploads() {
  const { data, error } = await supabase
    .from('uploads')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}

// Upload raw Excel file to Supabase Storage bucket 'excel-uploads'
// Returns the public URL so the admin can download the original file later.
export async function uploadExcelFile(buffer, filename, uploadId) {
  const blob = new Blob(
    [buffer],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  );
  const path = `${uploadId}_${filename}`;
  const { error } = await supabaseWrite.storage
    .from('excel-uploads')
    .upload(path, blob, { cacheControl: '3600', upsert: true });
  if (error) throw error;
  const { data } = supabaseWrite.storage.from('excel-uploads').getPublicUrl(path);
  return data.publicUrl;
}

export async function updateUploadFileUrl(uploadId, fileUrl) {
  await supabaseWrite
    .from('uploads')
    .update({ file_url: fileUrl })
    .eq('id', uploadId);
}

// Delete all Excel-imported reconciliation data for an upload:
// transactions and hub_data are removed; submissions (hub team proof entries)
// are intentionally preserved — their transaction_id becomes NULL via SET NULL FK.
// The upload record itself is kept so the audit trail is preserved.
// Useful when the wrong file was uploaded and you need to re-import clean data.
export async function deleteUploadData(uploadId) {
  // Step 1 — delete the transactions for this upload.
  // Submissions linked to these transactions will have their transaction_id
  // set to NULL automatically (SET NULL FK constraint) — they are NOT deleted.
  const { error: txnErr } = await supabaseWrite
    .from('transactions')
    .delete()
    .eq('upload_id', uploadId);
  if (txnErr) throw txnErr;

  // Step 2 — delete hub summary rows for this upload
  const { error: hubErr } = await supabaseWrite
    .from('hub_data')
    .delete()
    .eq('upload_id', uploadId);
  if (hubErr) throw hubErr;
}

// Delete only the upload record + its stored Excel file.
// hub_data, transactions, and submissions are intentionally preserved —
// they represent real hub team entries and must not be lost.
export async function deleteUpload(uploadId, filename, fileUrl) {
  // 1. Delete the Excel file from Supabase Storage
  // Try two methods: direct path (always works) and URL-parsed path (fallback)
  const storagePath = `${uploadId}_${filename}`;
  const { error: storageErr } = await supabaseWrite.storage
    .from('excel-uploads')
    .remove([storagePath]);

  if (storageErr) {
    // Fallback: try parsing path from the stored public URL
    if (fileUrl) {
      const marker = '/excel-uploads/';
      const idx = fileUrl.indexOf(marker);
      if (idx !== -1) {
        const urlPath = decodeURIComponent(fileUrl.slice(idx + marker.length).split('?')[0]);
        await supabaseWrite.storage.from('excel-uploads').remove([urlPath]);
      }
    }
  }

  // 2. Delete the upload record (upload_id on related rows becomes NULL via SET NULL FK)
  const { error } = await supabaseWrite
    .from('uploads')
    .delete()
    .eq('id', uploadId);
  if (error) throw error;
}

// ─── HUB DATA ─────────────────────────────────────────────────────────────────

export async function insertHubData(uploadId, hubs) {
  const rows = hubs.map(h => ({
    upload_id: uploadId,
    hub: h.hub,
    zone: h.zone,
    hub_type: h.hub_type || null,
    total_to_collect: h.totalToCollect,
    total_collected: h.totalCollected,
    total_deposited: h.totalDeposited,
    total_diff: h.totalDiff,
    open_to_collect: h.openToCollect ?? h.totalToCollect,
    open_deposited:  h.openDeposited  ?? h.totalDeposited,
    open_diff:       h.openDiff       ?? h.totalDiff,
    open_count: h.openCount,
    closed_count: h.closedCount,
    months: h.months,
  }));

  // hub_data has a UNIQUE(hub) constraint — one row per hub name.
  // Use upsert so that existing rows from previous uploads are updated in-place
  // (preserving their DB id) rather than failing with a duplicate-key error.
  // On conflict the upload_id and all aggregate columns are overwritten with
  // the new file's data; truly new hubs are inserted.
  const { error } = await supabaseWrite
    .from('hub_data')
    .upsert(rows, { onConflict: 'hub' });
  if (error) throw error;
}

// Fetch hub_data rows for a specific upload — bypasses v_latest_pendency so
// the upload_id is always correct and no stale rows from other uploads bleed in.
// Falls back to v_latest_pendency (all hubs) when no uploadId is supplied.
export async function getLatestHubData(uploadId) {
  if (uploadId) {
    const { data, error } = await supabase
      .from('hub_data')
      .select('*')
      .eq('upload_id', uploadId)
      .order('total_to_collect', { ascending: false })
      .limit(2000);
    if (error) throw error;
    return data || [];
  }
  // Fallback: no upload_id known yet — use view to get something to show
  const { data, error } = await supabase
    .from('v_latest_pendency')
    .select('*')
    .order('pendency', { ascending: false })
    .limit(1000);
  if (error) throw error;
  return data || [];
}

// ─── TRANSACTIONS ─────────────────────────────────────────────────────────────

export async function insertTransactions(uploadId, transactions) {
  const batchSize = 200;
  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize).map(t => ({
      upload_id: uploadId,
      hub: t.hub,
      sr_no: t.sr_no,
      collection_date: t.collection_date,
      month: t.month,
      zone: t.zone,
      hub_type: t.hub_type,
      amount_to_collect: t.amount_to_collect,
      collected: t.collected,
      deposited: t.deposited,
      diff: t.diff,
      status: t.status,
      line_status: t.line_status,
      transaction_id: t.transaction_id,
      partner_name: t.partner_name,
      cod_credit_1: t.cod_credit_1 || 0,
      cod_credit_2: t.cod_credit_2 || 0,
      dn_applied:   t.dn_applied != null ? t.dn_applied : null,
      dn_ref:       t.dn_ref   || null,
      dn_date:      t.dn_date  || null,
      bank_ref_1:   t.bank_ref_1 || null,
      ref_1:        t.ref_1     || null,
    }));

    // Rows without an Excel transaction_id.
    // Previously these were always inserted, which created new DB ids on every
    // upload — breaking submission links (submissions store the DB id as FK).
    // Fix: match existing rows by (hub, collection_date) and UPDATE them in-place
    // so the DB id is preserved and submissions stay linked.
    const withoutId = batch.filter(r => !r.transaction_id);
    if (withoutId.length > 0) {
      const hubs = [...new Set(withoutId.map(r => r.hub))];

      // Fetch existing non-direct transactions for these hubs from PREVIOUS uploads only.
      // We deliberately exclude the current uploadId so that rows already inserted or
      // updated in an earlier batch of THIS upload are invisible here.  If we included
      // them, a hub|date pair that spans a batch boundary (e.g. two rows for the same
      // date split across batch 3 and batch 4) would cause the second occurrence to be
      // incorrectly matched to the first-occurrence's freshly-inserted row, silently
      // dropping the second row instead of inserting it as a distinct split-date row.
      // By scoping to previous-upload rows only, each occurrence in the current file is
      // either matched to a stable historical DB row (UPDATE, preserving FK) or
      // inserted fresh (INSERT).  The amount_to_collect column is still used to prefer
      // an exact-amount match for split-date rows.
      const { data: existingRows, error: existingErr } = await supabaseWrite
        .from('transactions')
        .select('id, hub, collection_date, amount_to_collect')
        .in('hub', hubs)
        .neq('status', 'today_direct')
        .neq('upload_id', uploadId)   // ← only anchor rows from previous uploads
        .limit(20000);
      if (existingErr) console.warn('existingRows fetch error (non-fatal):', existingErr.message);

      // Build lookup: "hub|date" → [array of { id, amount_to_collect }]
      // Arrays allow multiple rows per date (split-date scenario).
      const existingByHubDate = {};
      (existingRows || []).forEach(t => {
        const key = `${t.hub}|${t.collection_date}`;
        if (!existingByHubDate[key]) existingByHubDate[key] = [];
        existingByHubDate[key].push({ id: t.id, amount_to_collect: t.amount_to_collect });
      });

      const toInsertNew  = [];
      const toUpdateExisting = [];
      withoutId.forEach(r => {
        const key = `${r.hub}|${r.collection_date}`;
        const candidates = existingByHubDate[key] || [];
        // Prefer an exact amount match so split-date rows each map to their own DB UUID.
        // If no amount match exists, fall back to the first available candidate.
        const byAmountIdx = candidates.findIndex(
          c => Number(c.amount_to_collect) === Number(r.amount_to_collect)
        );
        const matchIdx = byAmountIdx >= 0 ? byAmountIdx : (candidates.length > 0 ? 0 : -1);
        if (matchIdx >= 0) {
          toUpdateExisting.push({ dbId: candidates[matchIdx].id, row: r });
          // Remove this candidate so it can't be reused by a second row with the same amount
          existingByHubDate[key].splice(matchIdx, 1);
        } else {
          toInsertNew.push(r);
        }
      });

      if (toInsertNew.length > 0) {
        const { error } = await supabaseWrite.from('transactions').insert(toInsertNew);
        if (error) throw error;
      }

      // Update in-place — preserves DB id so existing submission FKs stay intact.
      // Also re-stamps upload_id so this row appears under the new upload.
      if (toUpdateExisting.length > 0) {
        const results = await Promise.all(
          toUpdateExisting.map(({ dbId, row }) =>
            supabaseWrite
              .from('transactions')
              .update(row)
              .eq('id', dbId)
          )
        );
        const updateErr = results.find(r => r.error)?.error;
        if (updateErr) throw updateErr;
      }
    }

    // Rows with a transaction_id — look up which already exist, update them,
    // and insert the rest.  If the batch INSERT hits a duplicate-key error (the
    // pre-check missed a row because the unique index is partial, or there was a
    // race), fall back to a per-row INSERT-or-UPDATE so nothing is lost.
    const withId = batch.filter(r => r.transaction_id);
    if (withId.length === 0) continue;

    const txnIds = withId.map(r => r.transaction_id);
    const { data: existing, error: txnLookupErr } = await supabaseWrite
      .from('transactions')
      .select('transaction_id')
      .in('transaction_id', txnIds)
      .limit(5000);
    if (txnLookupErr) console.warn('txnId lookup error (non-fatal):', txnLookupErr.message);

    const existingSet = new Set((existing || []).map(r => String(r.transaction_id)));
    const toInsert = withId.filter(r => !existingSet.has(String(r.transaction_id)));
    const toUpdate = withId.filter(r =>  existingSet.has(String(r.transaction_id)));

    // UPDATE existing rows in parallel — preserves DB id so submissions stay linked
    if (toUpdate.length > 0) {
      const updateResults = await Promise.all(
        toUpdate.map(row =>
          supabaseWrite
            .from('transactions')
            .update(row)
            .eq('transaction_id', row.transaction_id)
        )
      );
      const updateErr = updateResults.find(r => r.error)?.error;
      if (updateErr) throw updateErr;
    }

    // INSERT new rows.  If the batch fails with a duplicate-key error (partial
    // index means upsert can't be used, and the lookup can miss rows), retry
    // each row individually and UPDATE any that turn out to already exist.
    if (toInsert.length > 0) {
      const { error: batchInsertErr } = await supabaseWrite
        .from('transactions')
        .insert(toInsert);

      if (batchInsertErr) {
        if (batchInsertErr.code === '23505') {
          // Fallback: insert or update row by row
          const perRowResults = await Promise.all(
            toInsert.map(async row => {
              const { error: ie } = await supabaseWrite
                .from('transactions')
                .insert([row]);
              if (ie) {
                if (ie.code === '23505') {
                  // Row already exists — update it instead
                  return supabaseWrite
                    .from('transactions')
                    .update(row)
                    .eq('transaction_id', row.transaction_id);
                }
                return { error: ie };
              }
              return { error: null };
            })
          );
          const perRowErr = perRowResults.find(r => r.error)?.error;
          if (perRowErr) throw perRowErr;
        } else {
          throw batchInsertErr;
        }
      }
    }
  }
}

export async function getTransactionsForHub(hub, uploadId) {
  // Query the base transactions table directly so Hub Portal can recover
  // submissions client-side even when older submissions still point to an
  // earlier upload's transaction row.
  return fetchAllPaginatedRows(() => {
    let query = supabase
      .from('transactions')
      .select('*')
      .eq('hub', hub)
      .neq('status', 'today_direct')
      .order('collection_date', { ascending: true })
      .order('id', { ascending: true });

    if (uploadId) {
      query = query.eq('upload_id', uploadId);
    }

    return query;
  });
}

// ─── DIRECT SUBMISSIONS (untracked dates) ─────────────────────────────────────

// Returns all direct submissions for this hub grouped by date: { 'YYYY-MM-DD': [...subs] }
export async function getDirectSubmissions(uploadId, hub) { // eslint-disable-line no-unused-vars
  const { data: txns } = await supabase
    .from('transactions')
    .select('id, collection_date')
    .eq('hub', hub)
    .eq('status', 'today_direct');
  if (!txns || txns.length === 0) return {};
  const txnIds = txns.map(t => t.id);
  const txnDateMap = Object.fromEntries(txns.map(t => [t.id, t.collection_date]));
  const { data: subs, error } = await supabase
    .from('submissions')
    .select('*')
    .in('transaction_id', txnIds)
    .is('archived_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const result = {};
  for (const sub of (subs || [])) {
    const date = txnDateMap[sub.transaction_id];
    if (!result[date]) result[date] = [];
    result[date].push(sub);
  }
  return result;
}

// ─── SUBMISSIONS ──────────────────────────────────────────────────────────────

// Regular submission against an existing Excel transaction row
export async function addSubmission(uploadId, hub, transactionDbId, entry) {
  const txn = await getTransactionSnapshot(transactionDbId);
  const { data, error } = await supabaseWrite
    .from('submissions')
    .insert({
      transaction_id:  transactionDbId,
      hub,
      upload_id:       uploadId,
      type:            entry.type,
      amount:          entry.amount,
      ref_number:      entry.ref || null,
      submission_date: entry.date,
      remarks:         entry.remarks || null,
      slip_url:        entry.slipUrl || null,
      submitted_by:    entry.submittedBy || null,
      preserved_txn_date:              txn.collection_date || null,
      preserved_txn_amount_to_collect: txn.amount_to_collect ?? null,
      preserved_txn_diff:              txn.diff ?? null,
      preserved_txn_status:            txn.status || null,
      preserved_txn_upload_id:         txn.upload_id ?? null,
      preserved_excel_transaction_id:  txn.transaction_id || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Direct submission for an untracked date
export async function addDirectSubmission(uploadId, hub, date, entry) {
  // Find or create the today_direct placeholder transaction
  let txnId = null;
  const { data: existing } = await supabase
    .from('transactions')
    .select('id')
    .eq('hub', hub)
    .eq('collection_date', date)
    .eq('status', 'today_direct')
    .maybeSingle();

  if (existing) {
    txnId = existing.id;
  } else {
    const { data: newTxn, error: txnErr } = await supabaseWrite
      .from('transactions')
      .insert({
        upload_id: uploadId, hub,
        collection_date: date,
        diff: 0, amount_to_collect: 0, collected: 0, deposited: 0,
        status: 'today_direct',
      })
      .select('id')
      .single();
    if (txnErr) throw txnErr;
    txnId = newTxn.id;
  }

  const { data, error } = await supabaseWrite
    .from('submissions')
    .insert({
      transaction_id:  txnId,
      hub,
      upload_id:       uploadId,
      type:            entry.type,
      amount:          entry.amount,
      ref_number:      entry.ref || null,
      submission_date: entry.date,
      remarks:         entry.remarks || null,
      slip_url:        entry.slipUrl || null,
      submitted_by:    entry.submittedBy || null,
      preserved_txn_date:              date,
      preserved_txn_amount_to_collect: null,
      preserved_txn_diff:              null,
      preserved_txn_status:            'today_direct',
      preserved_txn_upload_id:         uploadId ?? null,
      preserved_excel_transaction_id:  null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getAllSubmissions() {
  const data = await fetchAllPaginatedRows(() =>
    supabase
      .from('submissions')
      .select('*, transactions(collection_date, diff, amount_to_collect, status)')
      .is('archived_at', null)
      .order('created_at', { ascending: false }),
  );
  return (data || []).map(flattenSubmission);
}

export async function getArchivedSubmissions() {
  const data = await fetchAllPaginatedRows(() =>
    supabase
      .from('submissions')
      .select('*, transactions(collection_date, diff, amount_to_collect, status)')
      .not('archived_at', 'is', null)
      .order('archived_at', { ascending: false })
      .order('created_at', { ascending: false }),
  );
  return (data || []).map(flattenSubmission);
}

// All transactions for the admin Summary + Reconciliation tabs.
// Queries the `transactions` table directly (not the view) so that every row
// is returned regardless of whether it has any submissions yet.
// The view `v_transactions_with_submissions` was INNER-JOINing submissions,
// which caused unsubmitted dates to be silently dropped from the hub summary.
// Filtering by uploadId scopes data to the current file.
// If no uploadId is provided, the function auto-detects the latest upload that
// actually has transaction rows (handles the case where a partial upload failure
// left uploadMeta pointing at an upload with no transactions).
export async function getAllTransactions(uploadId, knownHubs) {
  let uid = uploadId;

  if (!uid) {
    // Find the most recent upload_id that has transaction rows.
    // If knownHubs is supplied, prefer an upload whose transactions actually
    // use those hub names — this avoids picking a bad/partial upload (e.g. one
    // where all hubs were mis-parsed as "Unknown") when the hub summary is being
    // served from v_latest_pendency (the fallback view with older good data).
    let latest = null;
    if (knownHubs && knownHubs.length > 0) {
      const { data } = await supabase
        .from('transactions')
        .select('upload_id')
        .in('hub', knownHubs.slice(0, 10)) // sample known hubs to find the right upload
        .neq('status', 'today_direct')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      latest = data;
    }
    if (!latest) {
      // Fallback: latest upload with any transactions (excluding bad "Unknown"-only uploads)
      const { data } = await supabase
        .from('transactions')
        .select('upload_id')
        .neq('status', 'today_direct')
        .neq('hub', 'Unknown')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      latest = data;
    }
    if (!latest) return [];
    uid = latest.upload_id;
  }

  // Some Supabase projects enforce a hard API row cap of 1 000 per request.
  // Fetch in pages so large uploads (7k-8k+ rows) load fully in Reconciliation.
  return fetchAllPaginatedRows(() =>
    supabase
      .from('transactions')
      .select('*')
      .neq('status', 'today_direct')
      .eq('upload_id', uid)
      .order('collection_date', { ascending: false })
      .order('id', { ascending: false }),
  );
}

// Flag a submission for review — admin sends it back to the hub with a note
export async function flagSubmissionForReview(submissionId, note) {
  const { error } = await supabaseWrite
    .from('submissions')
    .update({ review_status: 'flagged', review_note: note || null })
    .eq('id', submissionId);
  if (error) throw error;
}

// Clear the review flag — admin un-flags (e.g. if they flagged by mistake)
export async function clearSubmissionReview(submissionId) {
  const { error } = await supabaseWrite
    .from('submissions')
    .update({ review_status: null, review_note: null })
    .eq('id', submissionId);
  if (error) throw error;
}

// Edit an existing submission in place — lets Admin/Control Tower correct a
// hub-submitted entry (amount, payment mode, ref, date, remarks) without
// needing to flag it and have the hub re-submit. Only touches the editable
// fields; leaves transaction linkage and the preserved txn snapshot alone.
// `fields.remarks` is taken as-is (the caller is responsible for keeping any
// [LOSS]/[SHORT]/[EXCESS] kind-prefix intact — those prefixes drive isLoss/
// isShort/isExcess detection elsewhere in the app, so this function must not
// mangle them the way an auto-added [EDITED] prefix would).
export async function updateSubmission(submissionId, fields) {
  const safe = {};
  if (fields.type !== undefined)    safe.type            = fields.type;
  if (fields.amount !== undefined)  safe.amount          = fields.amount;
  if (fields.ref !== undefined)     safe.ref_number      = fields.ref || null;
  if (fields.date !== undefined)    safe.submission_date = fields.date;
  if (fields.remarks !== undefined) safe.remarks         = fields.remarks || null;
  const { error } = await supabaseWrite
    .from('submissions')
    .update(safe)
    .eq('id', submissionId);
  if (error) throw error;
}

export async function deleteSubmission(submissionId, reason = 'user_deleted') {
  const { error } = await supabaseWrite
    .from('submissions')
    .update({
      archived_at: new Date().toISOString(),
      archived_by: 'app',
      archive_reason: reason,
      review_status: null,
      review_note: null,
    })
    .eq('id', submissionId);
  if (error) throw error;
}

export async function getSubmissionsForTransaction(transactionDbId) {
  const { data, error } = await supabase
    .from('submissions')
    .select('*')
    .eq('transaction_id', transactionDbId)
    .is('archived_at', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(flattenSubmission);
}

// All non-direct submissions for a hub, with the transaction join flattened.
// Used by HubPortal to compute effective totals client-side so that orphaned
// submissions (stale transaction_id from a pre-in-place-fix upload) are counted.
export async function getAllSubmissionsForHub(hub) {
  const data = await fetchAllPaginatedRows(() =>
    supabase
      .from('submissions')
      .select('*, transactions(collection_date, diff, amount_to_collect, status)')
      .eq('hub', hub)
      .is('archived_at', null)
      .order('created_at', { ascending: true }),
  );
  return (data || []).map(flattenSubmission);
}

// ─── STORAGE ──────────────────────────────────────────────────────────────────

export async function uploadSlip(file, hub) {
  const ext  = file.name.split('.').pop();
  const path = `${hub}/${Date.now()}.${ext}`;
  const { error } = await supabaseWrite.storage
    .from('deposit-slips')
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  const { data } = supabaseWrite.storage.from('deposit-slips').getPublicUrl(path);
  return data.publicUrl;
}

// ─── REALTIME ─────────────────────────────────────────────────────────────────

export function subscribeToSubmissions(uploadId, callback) {
  return supabase
    .channel('submissions-channel')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'submissions',
      filter: uploadId ? `upload_id=eq.${uploadId}` : undefined,
    }, callback)
    .subscribe();
}

// ─── HUB USERS (authentication) ───────────────────────────────────────────────

// SHA-256 hash using the browser Web Crypto API — no extra dependencies needed.
export async function hashPassword(password) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Verify credentials and return user record, or null if not found / inactive.
// Uses the anon client (SELECT only, no RLS on hub_users) so login works even
// when the service-role key is not available in the build environment.
export async function loginHubUser(username, passwordHash) {
  const { data, error } = await supabase
    .from('hub_users')
    .select('id, username, hub_name, is_active, role, managed_hubs')
    .eq('username', username.trim().toLowerCase())
    .eq('password_hash', passwordHash)
    .eq('is_active', true)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  // Parse managed_hubs from JSON string to array
  return {
    ...data,
    managed_hubs: data.managed_hubs ? JSON.parse(data.managed_hubs) : [],
    role: data.role || 'hub',
  };
}

// Re-fetch a hub user by ID to pick up any admin changes (role, managed_hubs, is_active, hub_name).
// Used on page load to sync the cached localStorage session with the latest DB state.
// Uses the anon client (read-only, no password needed).
export async function getHubUserById(id) {
  const { data, error } = await supabase
    .from('hub_users')
    .select('id, username, hub_name, is_active, role, managed_hubs')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    managed_hubs: data.managed_hubs ? JSON.parse(data.managed_hubs) : [],
    role: data.role || 'hub',
  };
}

// Admin — list all hub users (passwords excluded from select).
export async function getAllHubUsers() {
  const { data, error } = await supabaseWrite
    .from('hub_users')
    .select('id, username, hub_name, is_active, created_at, role, managed_hubs')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(u => ({
    ...u,
    managed_hubs: u.managed_hubs ? JSON.parse(u.managed_hubs) : [],
    role: u.role || 'hub',
  }));
}

// Admin — create a new hub user.
export async function createHubUser(username, passwordHash, hubName, role = 'hub', managedHubs = []) {
  const insert = {
    username: username.trim().toLowerCase(),
    password_hash: passwordHash,
    hub_name: hubName,
    role,
    managed_hubs: role === 'manager' ? JSON.stringify(managedHubs) : null,
  };
  const { data, error } = await supabaseWrite
    .from('hub_users')
    .insert(insert)
    .select('id, username, hub_name, is_active, created_at, role, managed_hubs')
    .single();
  if (error) throw error;
  return { ...data, managed_hubs: data.managed_hubs ? JSON.parse(data.managed_hubs) : [], role: data.role || 'hub' };
}

// Admin — update password, hub assignment, active status, role, or managed hubs.
export async function updateHubUser(id, { passwordHash, hubName, isActive, role, managedHubs } = {}) {
  const updates = {};
  if (passwordHash  !== undefined) updates.password_hash  = passwordHash;
  if (hubName       !== undefined) updates.hub_name       = hubName;
  if (isActive      !== undefined) updates.is_active      = isActive;
  if (role          !== undefined) updates.role           = role;
  if (managedHubs   !== undefined) updates.managed_hubs   = managedHubs === null ? null : JSON.stringify(managedHubs);
  const { error } = await supabaseWrite
    .from('hub_users')
    .update(updates)
    .eq('id', id);
  if (error) throw error;
}

// Manager — fetch submissions + transactions for a list of hubs (for the manager dashboard).
// Transactions are scoped to the most recent upload that actually contains these hubs,
// using the same smart detection as getAllTransactions() — prevents stale/wrong upload
// returning zero rows when a newer partial upload doesn't cover the manager's hubs.
export async function getManagerDashboardData(hubNames) {
  if (!hubNames || hubNames.length === 0) return { submissions: [], transactions: [], hubData: [], uploadMeta: null };

  // Step 1: find the most recent upload that actually has transaction rows for these hubs.
  // Sample up to 10 hub names to locate the right upload_id.
  let uploadId = null;
  let latestUpload = null;
  {
    const { data: hubTxn } = await supabase
      .from('transactions')
      .select('upload_id')
      .in('hub', hubNames.slice(0, 10))
      .neq('status', 'today_direct')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (hubTxn?.upload_id) {
      uploadId = hubTxn.upload_id;
      // Fetch upload meta for this upload_id
      const { data: um } = await supabase
        .from('uploads')
        .select('*')
        .eq('id', uploadId)
        .maybeSingle();
      latestUpload = um;
    } else {
      // Fallback: globally latest upload (no transactions found for these hubs at all)
      latestUpload = await getLatestUpload();
      uploadId = latestUpload?.id;
    }
  }

  // Submissions: use paginated fetch (same as getAllSubmissions) to avoid PostgREST's
  // 1000-row default cap — without this, managers with >1000 submissions would only
  // see their oldest entries and miss all recent data.
  const subsData = await fetchAllPaginatedRows(
    () => supabase.from('submissions')
      .select('*, transactions(collection_date, diff, amount_to_collect, status)')
      .in('hub', hubNames)
      .is('archived_at', null)
      .order('created_at', { ascending: true }),
    1000, 100000
  );

  const [txnRes, hubRes] = await Promise.all([
    // Transactions scoped to the hub-aware upload — avoids picking up a newer upload
    // that doesn't include this manager's hubs.
    uploadId
      ? supabase.from('transactions')
          .select('*')
          .in('hub', hubNames)
          .neq('status', 'today_direct')
          .eq('upload_id', uploadId)
          .order('collection_date', { ascending: true })
          .limit(20000)
      : Promise.resolve({ data: [], error: null }),
    supabase.from('hub_data')
      .select('*')
      .in('hub', hubNames)
      .order('created_at', { ascending: false }),
  ]);
  if (txnRes.error)   throw txnRes.error;
  if (hubRes.error)   throw hubRes.error;

  // Use the same flattenSubmission helper as getAllSubmissions() so that
  // preserved_txn_* fallback fields are honoured when the FK join returns null
  // (e.g. after a re-upload updates the linked transaction's upload_id).
  const submissions = (subsData || []).map(flattenSubmission);

  // Latest hub_data row per hub (highest created_at)
  const latestHubMap = {};
  (hubRes.data || []).forEach(h => {
    if (!latestHubMap[h.hub]) latestHubMap[h.hub] = h;
  });

  return {
    submissions,
    transactions: txnRes.data || [],
    hubData: Object.values(latestHubMap),
    uploadMeta: latestUpload,
  };
}

// Admin — permanently delete a hub user.
export async function deleteHubUser(id) {
  const { error } = await supabaseWrite
    .from('hub_users')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ─── PAYMENT SHEET ─────────────────────────────────────────────────────────────

/** Create a new payment upload record and return it.
 *  sheetId: Google Sheet ID for write-back support (null for Excel uploads). */
export async function createPaymentUpload(filename, period, sheetId = null) {
  const { data, error } = await supabaseWrite
    .from('payment_uploads')
    .insert({ filename, period, sheet_tab: 'ODH', sheet_id: sheetId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Bulk-insert payment rider rows (batchSize=300 to stay under Supabase limits). */
export async function insertPaymentRiders(uploadId, riders) {
  const batchSize = 300;
  for (let i = 0; i < riders.length; i += batchSize) {
    const batch = riders.slice(i, i + batchSize).map(r => ({ ...r, upload_id: uploadId }));
    const { error } = await supabaseWrite.from('payment_riders').insert(batch);
    if (error) throw error;
  }
}

/** Bulk-delete payment rider rows by id (used by the duplicate-row cleanup). */
export async function deletePaymentRiders(riderIds) {
  const batchSize = 300;
  for (let i = 0; i < riderIds.length; i += batchSize) {
    const { error } = await supabaseWrite
      .from('payment_riders')
      .delete()
      .in('id', riderIds.slice(i, i + batchSize));
    if (error) throw error;
  }
}

/** Fetch all payment uploads, newest first. */
export async function getPaymentUploads() {
  const { data, error } = await supabase
    .from('payment_uploads')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Record that this upload was just synced from its Google Sheet, so the
 * Payment Sheet UI can show "Last synced: ..." next to the Sync button.
 * Returns the new timestamp (ISO string) so the caller can update local state
 * without re-fetching the whole uploads list.
 */
export async function markUploadSynced(uploadId) {
  const now = new Date().toISOString();
  const { error } = await supabaseWrite
    .from('payment_uploads')
    .update({ last_synced_at: now })
    .eq('id', uploadId);
  if (error) throw error;
  return now;
}

/**
 * Fetch payment riders for a given upload, optionally filtered by hub(s).
 * hubNames: string[] for manager/hub-user scoping, null/undefined for admin (all).
 */
export async function getPaymentRiders(uploadId, hubNames, excludeHold = false) {
  // Payment-sheet hub names come from external files and may differ from the
  // user assignment only by casing. Filter normalized names after retrieval so
  // managers and hub users see every assigned hub consistently.
  const scopedHubNames = hubNames?.length
    ? new Set(hubNames.map(hub => String(hub || '').trim().toUpperCase()).filter(Boolean))
    : null;

  // For non-admin users, exclude rows where pmt_hold = 'Hold'.
  // Rows where pmt_hold is NULL (the vast majority) are always included.
  // Using neq so that PostgREST treats it as a strict inequality; NULL rows
  // pass through because NULL != 'Hold' is indeterminate → PostgREST includes
  // them when using or() with is.null as an explicit arm.
  const rows = await fetchAllPaginatedRows(() => {
    let q = supabase
      .from('payment_riders')
      .select('*')
      .eq('upload_id', uploadId)
      .order('hub_name', { ascending: true })
      .order('wm_name',  { ascending: true });
    if (excludeHold) q = q.or('pmt_hold.is.null,pmt_hold.neq.Hold');
    return q;
  }, 1000, 50000);

  if (!scopedHubNames) return rows;
  return rows.filter(rider => scopedHubNames.has(String(rider.hub_name || '').trim().toUpperCase()));
}

/**
 * Update ground-team-editable fields for a single rider row.
 * Only allows: advance, cod_loss, shipment_loss, scooty_rent, pmt_decision, remarks.
 */
export async function updatePaymentRiderFields(riderId, fields) {
  const allowed = ['advance', 'cod_loss', 'shipment_loss', 'shipment_loss2', 'scooty_rent', 'pmt_decision', 'remarks'];
  const safe = {};
  allowed.forEach(k => { if (Object.prototype.hasOwnProperty.call(fields, k)) safe[k] = fields[k]; });
  const now = new Date().toISOString();
  safe.updated_at = now;
  // fields_edited_at is a DIFFERENT timestamp from updated_at — see the comment
  // on BLANK_GUARD_WINDOW_MS below for why the two must not be the same column.
  safe.fields_edited_at = now;
  const { error } = await supabaseWrite
    .from('payment_riders')
    .update(safe)
    .eq('id', riderId);
  if (error) throw error;
}

/**
 * Fill account details for a rider — only succeeds if all account fields are currently blank.
 * The check is done client-side (the row is passed in) so we don't need a DB constraint.
 * updatedBy: username of the person filling the details.
 */
export async function updateRiderAccountDetails(riderId, details, updatedBy) {
  const { acct_holder, acct_number, bank_name, ifsc_code, pan_no } = details;
  const { error } = await supabaseWrite
    .from('payment_riders')
    .update({
      acct_holder,
      acct_number,
      bank_name,
      ifsc_code,
      pan_no,
      acct_updated_at: new Date().toISOString(),
      acct_updated_by: updatedBy || null,
      updated_at: new Date().toISOString(),
      fields_edited_at: new Date().toISOString(),
    })
    .eq('id', riderId);
  if (error) throw error;
}

/** Admin — update pmt_status for a rider. */
export async function updatePaymentRiderStatus(riderId, pmtStatus) {
  const { error } = await supabaseWrite
    .from('payment_riders')
    .update({ pmt_status: pmtStatus, updated_at: new Date().toISOString(), fields_edited_at: new Date().toISOString() })
    .eq('id', riderId);
  if (error) throw error;
}

/** Admin — manually lock or unlock a rider row (is_locked flag). */
export async function updatePaymentRiderLock(riderId, locked) {
  const { error } = await supabaseWrite
    .from('payment_riders')
    .update({ is_locked: locked, updated_at: new Date().toISOString() })
    .eq('id', riderId);
  if (error) throw error;
}

/** Admin — put a rider row on hold (hidden from ground team) or release it. */
export async function updatePaymentRiderHold(riderId, onHold) {
  const { error } = await supabaseWrite
    .from('payment_riders')
    .update({ pmt_hold: onHold ? 'Hold' : null, updated_at: new Date().toISOString() })
    .eq('id', riderId);
  if (error) throw error;
}

// These fields flow both ways: the app writes them back to the sheet (see
// FIELD_TO_COLUMN in the update-payment-sheet edge function), and a sync pulls
// them back in too, so an edit made directly in the sheet — or by someone else —
// shows up in the app. They must never be blanked out by a sync just because
// the freshly-fetched sheet row came back empty for them. Two ways that happens:
//  1. Race: the app just wrote e.g. "Yes" to Pmt Decision and fired the (unawaited)
//     write-back to the sheet, but a sync runs before that write-back lands — it
//     reads the sheet's still-old blank cell and would otherwise wipe out the
//     value the app already has correct.
//  2. A sheet header that doesn't exactly match what parser.js looks for reads back
//     as null every time — same effect.
// So: if the sheet has a value, it wins (this is how an edit made directly in the
// sheet, or by someone else, reaches the app). If the sheet comes back empty but the
// app already has a value, the app's value is kept rather than erased.
const NON_BLANKING_FIELDS = [
  'advance', 'cod_loss', 'shipment_loss', 'shipment_loss2', 'scooty_rent',
  'pmt_decision', 'remarks', 'pmt_status',
  'acct_holder', 'acct_number', 'bank_name', 'ifsc_code', 'pan_no',
];
// pan_status is intentionally NOT here — it's read-only in the app, sourced
// fresh from the sheet's STATUS column every sync, never written by the app,
// so there's nothing in the app that a blank sheet read could clobber.

// The blank-from-sheet guard above is only meant to cover the few-second window
// between the app saving a NON_BLANKING_FIELD and its unawaited write-back landing
// in the sheet — not to protect that value forever. Without a time bound, deleting
// a value directly in the Google Sheet (e.g. clearing "Advance" for a rider) would
// never make it into the app, no matter how many times someone hit Sync, because
// the app's old value would always "win" against the sheet's now-blank cell. So the
// guard only applies when the row was touched very recently in the app; once that
// window passes, a blank cell in the sheet is treated as a deliberate edit and is
// allowed to clear the field in the app, exactly like any other sheet value.
//
// IMPORTANT: this recency check must use `fields_edited_at`, a timestamp set ONLY
// by an actual human edit (updatePaymentRiderFields / updateRiderAccountDetails /
// updatePaymentRiderStatus) — never `updated_at`, which this very function also
// bumps to "now" on every row it touches regardless of whether anything changed.
// Using `updated_at` here was a bug: every sync re-armed its own guard for 5 more
// minutes on every row, so syncing more than once within 5 minutes (easy to do
// while testing, or just using the app normally) meant a value deleted directly in
// the sheet could never make it into the app — exactly the 2026-10-30 "Dipankar
// Phukan COD Loss won't clear" report. `fields_edited_at` is untouched by sync, so
// it only reflects a real person's last edit, and the window actually expires.
const BLANK_GUARD_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Sync riders from a freshly-fetched Google Sheet into an existing upload.
 * - Matches existing DB rows by sheet_row_index and updates all sheet-sourced fields.
 * - For NON_BLANKING_FIELDS, a blank/null value from the sheet never overwrites an
 *   existing non-blank value in the app (see comment above) — a non-blank sheet
 *   value always wins and does flow into the app.
 * - Preserves app-level metadata (acct_updated_by, acct_updated_at).
 * - Inserts any new rows that have no existing match.
 */
// Build a stable key from name+hub for riders with no Casper Employee ID on file.
// Not as strong an identity as the Casper ID, but far more stable than a sheet row
// number, which shifts under a row whenever anyone sorts, inserts, or deletes rows
// in the source Google Sheet.
function nameHubKey(r) {
  const name = String(r?.wm_name  || '').trim().toUpperCase();
  const hub  = String(r?.hub_name || '').trim().toUpperCase();
  return (name && hub) ? `${name}::${hub}` : null;
}

export async function syncPaymentRidersFromSheet(uploadId, riders) {
  // Fetch current riders so we can match them to the freshly-fetched sheet rows.
  // Also pull current values of the NON_BLANKING_FIELDS so we can guard against a
  // stale/blank sheet read erasing them.
  //
  // IMPORTANT: this must be paginated. A plain .limit(5000) still gets clamped to
  // PostgREST's server-side max-rows-per-request (commonly 1000), so with ~1000+
  // riders a single-page fetch silently came back short. Whichever rows fell off
  // that page looked "new" to the matcher below and got inserted as a duplicate —
  // on every single sync, since which rows fall on which page isn't guaranteed
  // stable. fetchAllPaginatedRows pages through with .range() until a page comes
  // back short, so every existing row is actually seen.
  const existing = await fetchAllPaginatedRows(() =>
    supabase
      .from('payment_riders')
      .select(`id, sheet_row_index, casper_employee_id, wm_name, hub_name, acct_updated_by, acct_updated_at, fields_edited_at, ${NON_BLANKING_FIELDS.join(', ')}`)
      .eq('upload_id', uploadId),
    1000, 50000
  );

  // IMPORTANT: a Casper Employee ID is NOT unique per row in these sheets — the
  // same rider can legitimately appear twice in one period if they worked out of
  // two different hubs (each hub gets its own payout line, with its own Total
  // Attempted/Amount/etc). So identity for matching — and for anything that
  // decides two rows are "the same" — must be Casper ID + Hub, never Casper ID
  // alone. (A previous version of this matching, and the duplicate-cleanup tool
  // that used it, treated Casper ID alone as identity and collapsed a rider's
  // two legitimate hub rows into one, silently deleting the other hub's data.)
  function casperHubKey(r) {
    const casper = String(r?.casper_employee_id || '').trim();
    const hub    = String(r?.hub_name           || '').trim().toUpperCase();
    return (casper && hub) ? `${casper}::${hub}` : null;
  }

  const byRow       = {};
  const byCasperHub = {};
  const byNameHub   = {};
  (existing || []).forEach(r => {
    if (r.sheet_row_index) byRow[r.sheet_row_index] = r;
    const ch = casperHubKey(r);
    if (ch) byCasperHub[ch] = r;
    const nh = nameHubKey(r);
    if (nh) byNameHub[nh] = r;
  });

  const toInsert = [];
  const toUpdate = [];

  for (const rider of riders) {
    // Match by a STABLE identity first — Casper Employee ID + Hub, then
    // name+hub — and only fall back to the sheet row number as a last resort.
    // The row number is just a position, not an identity: the moment someone
    // sorts, inserts, or deletes a row in the sheet, "row 15" can belong to a
    // completely different rider than it did at the last sync. Matching on row
    // number first (the old behaviour) meant a reorder could silently merge one
    // rider's DB row into another rider's identity — e.g. rider A's old remarks
    // surviving under rider B's name after B took over A's old row number.
    const match = (casperHubKey(rider) && byCasperHub[casperHubKey(rider)])
               || (nameHubKey(rider)   && byNameHub[nameHubKey(rider)])
               || (rider.sheet_row_index && byRow[rider.sheet_row_index]);
    if (match) {
      const fields = { ...rider };
      // Guard: don't let an empty sheet read blank out a NON_BLANKING_FIELD that
      // already has a value in the app — but only within a short window after the
      // app last touched this row (see BLANK_GUARD_WINDOW_MS above). Outside that
      // window, a blank cell in the sheet is a deliberate edit made directly in the
      // sheet and is allowed through, just like any non-blank sheet value is.
      const recentlyTouchedInApp = match.fields_edited_at &&
        (Date.now() - new Date(match.fields_edited_at).getTime()) < BLANK_GUARD_WINDOW_MS;
      NON_BLANKING_FIELDS.forEach(f => {
        if ((fields[f] === null || fields[f] === undefined || fields[f] === '') && match[f] != null && match[f] !== '' && recentlyTouchedInApp) {
          fields[f] = match[f];
        }
      });
      toUpdate.push({
        dbId: match.id,
        fields: {
          ...fields,
          upload_id: uploadId,
          // Preserve who filled account details — that metadata lives only in the app
          acct_updated_by: match.acct_updated_by,
          acct_updated_at: match.acct_updated_at,
          updated_at: new Date().toISOString(),
        },
      });
    } else {
      toInsert.push({ ...rider, upload_id: uploadId });
    }
  }

  // Update matched rows in parallel (batched at 20 concurrent requests)
  for (let i = 0; i < toUpdate.length; i += 20) {
    await Promise.all(
      toUpdate.slice(i, i + 20).map(({ dbId, fields }) =>
        supabaseWrite.from('payment_riders').update(fields).eq('id', dbId)
      )
    );
  }

  // Insert new rows
  const batchSize = 300;
  for (let i = 0; i < toInsert.length; i += batchSize) {
    const { error } = await supabaseWrite.from('payment_riders').insert(toInsert.slice(i, i + batchSize));
    if (error) throw error;
  }
}

/**
 * One-off backfill: push every rider's currently-set app-owned fields back to the
 * source Google Sheet. Use this after write-back was broken for a while (e.g. the
 * 2026-09-28 incident where a protected range on the sheet silently rejected every
 * write) and the database has correct values the sheet never received.
 *
 * riders: the full rider list for the upload (already loaded in the app).
 * onProgress(done, total): optional, called after each rider so the UI can show a
 * progress indicator.
 * Runs sequentially with a short pause between requests to stay comfortably under
 * Google Sheets API rate limits — with a few hundred rows this takes well under a
 * couple of minutes.
 * Returns { total, pushed, failed: [{ rider, hub, error }] }.
 */
export async function backfillRidersToSheet(upload, riders, onProgress) {
  if (!upload?.sheet_id) throw new Error('This upload has no linked Google Sheet.');

  const targets = (riders || []).filter(r =>
    r.sheet_row_index && NON_BLANKING_FIELDS.some(f => r[f] != null && r[f] !== '')
  );
  const results = { total: targets.length, pushed: 0, failed: [] };

  for (const r of targets) {
    const fields = {};
    NON_BLANKING_FIELDS.forEach(f => { if (r[f] != null && r[f] !== '') fields[f] = r[f]; });
    try {
      const { data, error } = await supabase.functions.invoke('update-payment-sheet', {
        body: {
          sheetId:          upload.sheet_id,
          tabName:          'ODH',
          rowIndex:         r.sheet_row_index,
          casperEmployeeId: r.casper_employee_id || undefined,
          hubName:          r.hub_name || undefined,
          fields,
        },
      });
      if (error) throw new Error(error.message || 'Edge function error');
      if (data?.error) throw new Error(data.error);
      results.pushed += 1;
    } catch (e) {
      results.failed.push({ rider: r.wm_name, hub: r.hub_name, error: e.message });
    }
    onProgress?.(results.pushed + results.failed.length, results.total);
    // Gentle pacing — one request roughly every 200ms — to avoid tripping Google's
    // per-minute write quota when backfilling a few hundred rows at once.
    await new Promise(res => setTimeout(res, 200));
  }

  return results;
}

// ─── PAYMENT AUDIT LOG ────────────────────────────────────────────────────────

/**
 * Fire-and-forget audit log entry. Never throws — log failures are silent.
 * entry: { uploadId, riderId, riderName, hubName, casperId, action, fieldName, oldValue, newValue, doneBy }
 */
export function logPaymentAction(entry) {
  supabaseWrite
    .from('payment_audit_log')
    .insert({
      upload_id:          entry.uploadId  || null,
      rider_id:           entry.riderId   || null,
      rider_name:         entry.riderName || null,
      hub_name:           entry.hubName   || null,
      casper_employee_id: entry.casperId  || null, // FHRID — stable rider ID, for easy search
      action:     entry.action,
      field_name: entry.fieldName || null,
      old_value:  entry.oldValue  != null ? String(entry.oldValue) : null,
      new_value:  entry.newValue  != null ? String(entry.newValue) : null,
      done_by:    entry.doneBy    || 'unknown',
    })
    .then(({ error }) => { if (error) console.warn('Audit log write failed:', error.message); });
}

/**
 * Fetch ALL audit log entries for a given upload (admin only), newest-first.
 * Supabase/PostgREST caps any single request at its own max row count (commonly
 * 1000), so a plain .select() silently truncates once the log grows past that —
 * this paginates with .range() until a page comes back short, to return every
 * entry regardless of how many there are.
 */
export async function getPaymentAuditLog(uploadId) {
  const PAGE_SIZE = 1000;
  let all = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let q = supabase
      .from('payment_audit_log')
      .select('*')
      .order('done_at', { ascending: false })
      .range(from, from + PAGE_SIZE - 1);
    if (uploadId) q = q.eq('upload_id', uploadId);
    const { data, error } = await q;
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break; // last page
    from += PAGE_SIZE;
  }
  return all;
}

/** Admin — delete a payment upload (cascades to riders). */
export async function deletePaymentUpload(uploadId) {
  const { error } = await supabaseWrite
    .from('payment_uploads')
    .delete()
    .eq('id', uploadId);
  if (error) throw error;
}

// ─── BILLING / PAYOUT SHEET ────────────────────────────────────────────────

/** Fetch all billing batch metadata, newest first. */
export async function getBillingBatches() {
  const { data, error } = await supabase
    .from('billing_batches_meta')
    .select('*')
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Fetch all raw billing rows for a batch (paginated). */
export async function getBillingRawRows(batchId) {
  return fetchAllPaginatedRows(
    () => supabase
      .from('billing_raw_rows')
      .select('raw_data')
      .eq('batch_id', batchId),
    1000,
    50000
  );
}

/** Fetch ALL blinkit_rider_meta records (for master riders management). */
export async function getAllBlinkitRiderMeta() {
  return fetchAllPaginatedRows(
    () => supabase
      .from('blinkit_rider_meta')
      .select('*')
      .order('rider_name', { ascending: true }),
    1000,
    100000
  );
}

/** Delete a rider from master data. */
export async function deleteBlinkitRiderMeta(driverId) {
  const { error } = await supabaseWrite
    .from('blinkit_rider_meta')
    .delete()
    .eq('driver_id', driverId);
  if (error) throw error;
}

/** Fetch blinkit_rider_meta for a list of driver IDs. */
export async function getBlinkitRiderMeta(driverIds) {
  if (!driverIds || driverIds.length === 0) return [];
  const { data, error } = await supabase
    .from('blinkit_rider_meta')
    .select('*')
    .in('driver_id', driverIds);
  if (error) throw error;
  return data || [];
}

/** Fetch blinkit_payout_rows for a given batch. */
export async function getBlinkitPayoutRows(batchId) {
  const { data, error } = await supabase
    .from('blinkit_payout_rows')
    .select('*')
    .eq('batch_id', batchId);
  if (error) throw error;
  return data || [];
}

/** Upsert a rider meta record (keyed by driver_id). */
export async function upsertBlinkitRiderMeta(meta) {
  const { error } = await supabaseWrite
    .from('blinkit_rider_meta')
    .upsert({ ...meta, updated_at: new Date().toISOString() }, { onConflict: 'driver_id' });
  if (error) throw error;
}

/** Upsert a payout row record (keyed by batch_id + driver_id). */
export async function upsertBlinkitPayoutRow(row) {
  const { error } = await supabaseWrite
    .from('blinkit_payout_rows')
    .upsert({ ...row, updated_at: new Date().toISOString() }, { onConflict: 'batch_id,driver_id' });
  if (error) throw error;
}
